// The consent checkboxes and the spoken voice-clone statement, kept apart from the policy text so the upload, Suno and
// voice screens can use them without loading every page. content.ts re-exports them; legal.test.ts checks they say what
// the Terms and the Voice and Likeness Policy say.

/**
 * The Terms version a user accepts at sign-up (v4 LG2): the date of the Terms it stands for (never later than content.ts
 * UPDATED; signup-gate.test.ts checks). The api mirrors it (api/src/signup.ts TERMS_VERSION; its test checks they match) and stores the version
 * and time per user. Change it when the Terms or the Acceptable Use Policy change materially: everyone is asked once
 * more on their next visit.
 */
export const TERMS_VERSION = "2026-10-29";

/** The sign-up checkbox (unticked until the user ticks it): the Terms and the Acceptable Use Policy are agreed to,
 *  the Privacy Policy is linked as a notice. */
export const SIGNUP_TERMS_CONSENT = {
  agree: { before: "I agree to the", terms: { text: "Terms", href: "/terms" }, and: "and the", aup: { text: "Acceptable Use Policy", href: "/acceptable-use" } },
  privacy: { before: "Read how we handle your information in the", link: { text: "Privacy Policy", href: "/privacy" } },
  age: "You must be 18 or older.",
  /** Wave 1 safety: the second box, beside the Terms one (both required; POST me/accept-terms sends adult: true) */
  adult: "I'm 18 or older.",
} as const;

/** A checkbox the upload UI shows: its label, and where "Read the Terms" goes. */
export type ConsentCopy = { label: string; link: { text: string; href: string } };

/**
 * The one-time checkbox before a user's first upload (the upload sheet; the api records `users.upload_terms_at`).
 * It restates the Terms' "Your uploads" and "Photos of real people" paragraphs (Q8: real people only with
 * permission, no public figures, no children).
 */
export const UPLOAD_TERMS_CONSENT: ConsentCopy = {
  label: "I own these files or have permission to use them. Any photo of a real person is me or someone who said yes, and never a public figure or anyone under 18.",
  link: { text: "Read the Terms", href: "/terms#your-content" },
};

/** The checkbox shown each time a photo of a person becomes a character's starting point. */
export const PERSON_PHOTO_CONSENT: ConsentCopy = {
  label: "This is me, or I have this person's permission. They're not a public figure and they're 18 or older.",
  link: { text: "Read the Terms", href: "/terms#your-content" },
};

/** The checkbox before recording a voice sample (v4.1 "Narrate in my own voice"). */
export const VOICE_CLONE_CONSENT: ConsentCopy = {
  label: "This is my own voice. I agree to CAO Studio and ElevenLabs making a private AI copy of it for my videos. I can delete it at any time.",
  link: { text: "Read the Voice and Likeness Policy", href: "/voice-likeness#your-voice" },
};

/** What the user reads aloud, in the voice being cloned, before the sample is accepted. `name` is the account's name. */
export function voiceCloneStatement(name: string): string {
  const who = name.trim() || "the owner of this account";
  return `My name is ${who}, and this is my own voice. I agree that CAO Studio and ElevenLabs can use this recording to make an AI copy of my voice that only my account can use, to narrate my videos, including in other languages. I know I can delete it at any time.`;
}
